import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { accountPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityAccountPage } from '@data/sanity/queries/types/page'
import { type SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { defaultModuleQueryParameters, getAccountPage } from '@lib/sanity/page'
import { getProductCatalogue } from '@lib/sanity/product'
import { useUser } from '@lib/auth'
import { type Locale } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext, getLinkPageUrl } from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type AccountPageProps = BasePageProps & {
  locale: Locale
  page: SanityAccountPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
}

const AccountPage = ({ draftMode, locale, page, site }: AccountPageProps) => {
  const router = useRouter()
  const { user } = useUser()

  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  // Redirect to login page if not logged in
  useEffect(() => {
    if (user && !user.isLoggedIn) {
      const url = getLinkPageUrl('loginPage')
      router.push(url, url, {
        locale,
      })
    }
  }, [draftMode, locale, router, user])

  if (!user?.isLoggedIn) {
    return null
  }

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={accountPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<AccountPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const accountPage = await getAccountPage(sanityClient, context.locale)

  if (!accountPage.page) {
    return {
      notFound: true,
    }
  }

  // Get all products
  const productCatalogue = await getProductCatalogue(
    sanityClient,
    context.locale,
  )

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(accountPage.page),
      site: getNonNullValues(accountPage.site),
      productCatalogue,
    },
    revalidate: 60,
  }
}

export default AccountPage
